import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { noop, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import {
  baseAdministrationSelectors,
  BaseCoreState,
  BaseFilterService,
  BaseListPagination,
  BaseModalService,
  BaseQueryParamService,
  baseRelatedPspBranchActions,
  baseRelatedPspBranchesSelectors,
  BaseRelatedPspPayloadInterface,
  BranchListItemResponse,
  CustomerBranchListItemResponse,
  LocationListItemResponse,
} from 'base';

@Component({
  selector: 'base-customer-provider-list',
  templateUrl: './base-customer-provider-list.component.html',
})
export class BaseCustomerProviderListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('providerDetailsTemplate') providerDetailsTemplate: ElementRef;

  providers: CustomerBranchListItemResponse[] = [];
  selectedProvider: CustomerBranchListItemResponse | undefined;
  locations: { value: string; name: string }[] = [];
  page: number = BaseListPagination.page;
  size: number = BaseListPagination.size;
  itemsCount = 0;
  filterValues: { [key in any]: any } = { location: [] };
  detailsModalRef: NgbModalRef;
  isIZSInfoTab: boolean = false;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private queryParamsService: BaseQueryParamService,
    private modalService: BaseModalService,
    private router: Router,
    public filterService: BaseFilterService,
    public activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadRelatedBranches();

    this.store
      .select(baseRelatedPspBranchesSelectors.getRelatedPspBranchList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.providers = state.data as BranchListItemResponse[];
        this.itemsCount = state.pagination?.page.itemsCount as number;
        this.size = state.pagination?.page.size as number;
        this.page = state.pagination?.page.current as number;
      });

    this.store
      .select(baseAdministrationSelectors.getLocationsData)
      .pipe(
        map((state) => state.data as LocationListItemResponse[]),
        takeUntil(this.destroy$)
      )
      .subscribe((locationsData) => {
        this.locations = (locationsData || []).map((location) => ({
          value: location.id.toString(),
          name: location.name,
        }));
      });
  }

  ngAfterViewInit() {
    this.checkBranchIdForIZS();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  checkBranchIdForIZS(): void {
    let queryParams = { ...this.activatedRoute.snapshot.queryParams };
    if (!!queryParams.pspBranchId) {
      const pspBranchId = queryParams.pspBranchId;
      this.selectedProvider = this.providers.find((provider) => provider.id === +pspBranchId);

      delete queryParams.pspBranchId;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        queryParamsHandling: '',
      });

      if (this.selectedProvider) {
        this.openProviderDetailModal(this.selectedProvider, true);
      }
    }
  }

  openProviderDetailModal(provider: CustomerBranchListItemResponse, isIZSInfoTab: boolean = false): void {
    this.isIZSInfoTab = isIZSInfoTab;
    this.selectedProvider = provider;
    this.store.dispatch(
      baseRelatedPspBranchActions.loadRelatedPspBranchDetails({
        payload: {
          branch: this.selectedProvider.id as number,
          psp: this.selectedProvider.pspId as number,
          location: this.selectedProvider.customerLocation?.id as number,
        },
      })
    );
    this.store.dispatch(
      baseRelatedPspBranchActions.loadRelatedPspBranchDocuments({
        payload: { psp: this.selectedProvider.pspId as number },
      })
    );
    this.store.dispatch(baseRelatedPspBranchActions.loadSurcharges({ payload: this.selectedProvider.pspId as number }));
    this.store.dispatch(
      baseRelatedPspBranchActions.loadRelatedPspBranchData({ payload: { psp: this.selectedProvider.pspId as number } })
    );

    this.detailsModalRef = this.modalService.open(
      {
        bodyTemplate: this.providerDetailsTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.detailsModalRef.result.then(noop, noop);
  }

  openIZSInfoModal(event: { event: Event; data: CustomerBranchListItemResponse }): void {
    event.event.stopPropagation();
    this.openProviderDetailModal(event.data, true);
  }

  private loadRelatedBranches(): void {
    this.queryParamsService.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
      this.queryParamsService.redirectWithQueryParams(
        value,
        {
          search: 'value',
        },
        () => this.fetchRelatedPspBranches()
      );
    });
  }

  private fetchRelatedPspBranches(): void {
    this.store.dispatch(
      baseRelatedPspBranchActions.loadRelatedPspBranchList({
        payload: this.activatedRoute.snapshot.queryParams as BaseRelatedPspPayloadInterface,
      })
    );
  }
}
